import { useState, useEffect } from 'react';
import { FaShoppingCart, FaPlus, FaEdit, FaTrash, FaFilter, FaBox, FaTags, FaUsers, FaEye, FaEyeSlash, FaIdCard, FaTools, FaUserTie } from 'react-icons/fa';
import supabase from '../../supabaseClient';
import { useAuth } from '../../hooks/useAuth';

const ProductManagement = () => {
  // Estado para as categorias de produtos
  const [categories, setCategories] = useState([]);
  // Estado para armazenar a lista de produtos
  const [products, setProducts] = useState([]);
  // Estado para o carregamento de dados
  const [loading, setLoading] = useState(true);
  // Estado para mensagens de erro
  const [error, setError] = useState(null);

  // Estado para o formulário de adicionar/editar produto
  const [formData, setFormData] = useState({
    id: null,
    nome: '',
    descricao: '',
    preco: '',
    categoria_id: 1,
    estoque: 0,
    ativo: true,
    visivel_no_catalogo: true,
    vinculado_credencial_expositor: false,
    vinculado_montador: false,
    vinculado_prestador: false // NOVO CAMPO
  });
  
  // Estado para controlar se estamos editando ou adicionando
  const [isEditing, setIsEditing] = useState(false);
  // Estado para controlar o modal
  const [showModal, setShowModal] = useState(false);
  // Estado para filtrar produtos por categoria
  const [filterCategory, setFilterCategory] = useState(0);
  // Estado para controlar a aba ativa no modal
  const [activeTab, setActiveTab] = useState('dados');
  // Obter contexto de autenticação
  const { hasPermission } = useAuth();
  
  // Carregar categorias do Supabase
  const fetchCategories = async () => {
    try {
      console.log('Iniciando carregamento de categorias...');
      
      const { data, error } = await supabase
        .from('caex_categorias')
        .select('*')
        .order('id', { ascending: true });
      
      if (error) {
        console.error('Erro ao buscar categorias:', error);
        throw error;
      }
      
      console.log('Categorias carregadas (raw):', data);
      
      // Força a conversão e normalização das categorias
      const normalizedCategories = Array.isArray(data) ? data.map(cat => ({
        id: Number(cat.id),
        nome: String(cat.nome || `Categoria ${cat.id}`),
        descricao: cat.descricao || '',
        ativo: Boolean(cat.ativo)
      })) : [];
      
      console.log('Categorias normalizadas:', normalizedCategories);
      
      if (normalizedCategories.length === 0) {
        console.warn('Nenhuma categoria encontrada no banco de dados.');
      }
      
      setCategories(normalizedCategories);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
      setError('Não foi possível carregar as categorias de produtos.');
      setCategories([]);
    }
  };

  // Carregar produtos do Supabase
  const fetchProducts = async () => {
    try {
      setLoading(true);
      console.log('Iniciando carregamento de produtos...');
      
      const { data, error } = await supabase
        .from('caex_produtos')
        .select('*')
        .order('id', { ascending: false });
      
      if (error) {
        console.error('Erro ao buscar produtos:', error);
        throw error;
      }
      
      console.log('Produtos carregados:', data);
      
      if (!data || data.length === 0) {
        console.warn('Nenhum produto encontrado no banco de dados.');
      }
      
      setProducts(data || []);
      setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar produtos:', error);
      setError('Não foi possível carregar a lista de produtos.');
      setLoading(false);
    }
  };

  // Definir categorias iniciais para debug e garantir que sempre haja pelo menos uma opção
  useEffect(() => {
    // Definir uma categoria padrão inicial
    const categoriaPadrao = {
      id: 1,
      nome: 'TESTE CATEGORIA',
      descricao: 'TESTE',
      ativo: true
    };
    
    setCategories([categoriaPadrao]);
    console.log('Categoria padrão definida:', categoriaPadrao);
    
    // Continuar com o carregamento normal das categorias do banco de dados
    fetchCategories();
    // Carregar produtos
    fetchProducts();
  }, []);
  
  // Estilos para o modal flutuante
  const modalStyles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '1rem',
      backdropFilter: 'blur(5px)'
    },
    content: {
      position: 'relative',
      width: '100%',
      maxWidth: '650px',
      maxHeight: '90vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'white',
      borderRadius: '20px',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      overflow: 'hidden',
      animationName: 'fadeInUp',
      animationDuration: '0.4s',
      animationTimingFunction: 'cubic-bezier(0.175, 0.885, 0.32, 1.275)',
      animationFillMode: 'both'
    },
    header: {
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
      padding: '1.5rem 2rem',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexShrink: 0
    },
    closeButton: {
      width: '36px',
      height: '36px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%',
      border: 'none',
      background: 'white',
      color: '#6c757d',
      fontSize: '1rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
    },
    title: {
      fontSize: '1.25rem',
      fontWeight: '600',
      color: '#1a1a1a',
      margin: '0',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem'
    },
    tabs: {
      display: 'flex',
      borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
      backgroundColor: 'white',
      flexShrink: 0
    },
    tab: {
      padding: '1rem 1.5rem',
      fontSize: '0.95rem',
      fontWeight: '500',
      color: '#6c757d',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      position: 'relative'
    },
    activeTab: {
      color: '#0d6efd',
      fontWeight: '600'
    },
    activeTabIndicator: {
      position: 'absolute',
      bottom: '-1px',
      left: '0',
      right: '0',
      height: '2px',
      backgroundColor: '#0d6efd'
    },
    body: {
      padding: '2rem',
      overflowY: 'auto',
      flex: '1 1 auto'
    },
    footer: {
      display: 'flex',
      justifyContent: 'flex-end',
      padding: '1.5rem 2rem',
      borderTop: '1px solid rgba(0, 0, 0, 0.05)',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
      flexShrink: 0
    },
    footerButton: {
      padding: '0.75rem 1.5rem',
      borderRadius: '50px',
      fontSize: '0.9rem',
      fontWeight: '600',
      transition: 'all 0.3s ease',
      cursor: 'pointer'
    },
    cancelButton: {
      border: '1px solid #dee2e6',
      background: 'white',
      color: '#495057',
      marginRight: '0.75rem'
    },
    submitButton: {
      border: 'none',
      background: '#212529',
      color: 'white',
      boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)'
    },
    formGroup: {
      marginBottom: '1.5rem'
    },
    formLabel: {
      display: 'block',
      marginBottom: '0.5rem',
      fontSize: '0.9rem',
      fontWeight: '600',
      color: '#495057'
    },
    formInput: {
      width: '100%',
      padding: '0.75rem 1rem',
      border: '1px solid #dee2e6',
      borderRadius: '10px',
      fontSize: '0.9rem',
      color: '#495057',
      transition: 'all 0.3s ease'
    },
    requiredField: {
      color: '#dc3545',
      marginLeft: '0.25rem'
    },
    requiredNotice: {
      fontSize: '0.8rem',
      color: '#dc3545',
      marginBottom: '1rem'
    }
  };

  // Adicionar estilo de animação ao carregar o componente
  useEffect(() => {
    // Criar elemento de estilo para a animação
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes fadeInUp {
        from { 
          opacity: 0; 
          transform: translateY(30px);
        }
        to { 
          opacity: 1; 
          transform: translateY(0);
        }
      }
      
      .form-input {
        width: 100%;
        padding: 0.75rem 1rem;
        border: 1px solid #dee2e6;
        border-radius: 10px;
        font-size: 0.9rem;
        color: #495057;
        transition: all 0.3s ease;
      }
      
      .form-input:focus {
        outline: none;
        border-color: #212529;
        box-shadow: 0 0 0 3px rgba(33, 37, 41, 0.1);
      }
      
      .form-label {
        display: block;
        margin-bottom: 0.5rem;
        font-size: 0.9rem;
        font-weight: 600;
        color: #495057;
      }
      
      .form-group {
        margin-bottom: 1.5rem;
      }
      
      .modal-close-btn:hover {
        background-color: #f8f9fa;
        color: #212529;
        transform: rotate(90deg);
      }
      
      .btn-cancel:hover {
        background-color: #f8f9fa;
      }
      
      .btn-submit:hover {
        background-color: #343a40;
        transform: translateY(-2px);
        box-shadow: 0 6px 15px rgba(0, 0, 0, 0.15);
      }
      
      .modal-tab {
        position: relative;
        padding: 1rem 1.5rem;
        font-weight: 500;
        color: #6c757d;
        cursor: pointer;
        transition: all 0.3s ease;
      }
      
      .modal-tab-active {
        color: #0d6efd;
        font-weight: 600;
      }
      
      .modal-tab-active::after {
        content: '';
        position: absolute;
        bottom: -1px;
        left: 0;
        right: 0;
        height: 2px;
        background-color: #0d6efd;
      }
      
      .modal-tab:hover:not(.modal-tab-active) {
        color: #495057;
        background-color: rgba(0, 0, 0, 0.02);
      }
      
      /* Estilos para a tabela moderna */
      .modern-table-card {
        background-color: #fff;
        border-radius: 15px;
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
        overflow: hidden;
        margin-bottom: 2rem;
        border: 1px solid rgba(0, 0, 0, 0.05);
      }
      
      .modern-card-header {
        background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
        padding: 1.25rem 1.5rem;
        border-bottom: 1px solid rgba(0, 0, 0, 0.05);
      }
      
      .modern-card-title {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        margin: 0;
        font-size: 1.15rem;
        font-weight: 600;
        color: #212529;
      }
      
      .card-title-icon {
        color: #6c757d;
      }
      
      .modern-card-body {
        padding: 0;
      }
      
      .modern-table-container {
        overflow-x: auto;
      }
      
      .modern-table {
        width: 100%;
        border-collapse: collapse;
      }
      
      .modern-table th,
      .modern-table td {
        padding: 1rem 1.5rem;
        text-align: left;
        border-bottom: 1px solid rgba(0, 0, 0, 0.05);
      }
      
      .modern-table th {
        background-color: #f8f9fa;
        font-weight: 600;
        color: #495057;
        font-size: 0.9rem;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      
      .modern-table tr:last-child td {
        border-bottom: none;
      }
      
      .modern-table tr:hover {
        background-color: rgba(0, 0, 0, 0.01);
      }
      
      .product-name {
        font-weight: 600;
        color: #212529;
      }
      
      .text-right {
        text-align: right;
      }
      
      /* Estilo para os badges de vinculação */
      .link-badge {
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
        padding: 0.25rem 0.5rem;
        border-radius: 15px;
        font-size: 0.7rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-right: 0.25rem;
        margin-bottom: 0.25rem;
      }
      
      .link-credencial {
        background-color: rgba(0, 123, 255, 0.15);
        color: #007bff;
      }
      
      .link-montador {
        background-color: rgba(255, 193, 7, 0.15);
        color: #ffc107;
      }
      
      .link-prestador {
        background-color: rgba(40, 167, 69, 0.15);
        color: #28a745;
      }
      
      /* Estilo para o novo campo de visibilidade para expositor */
      .visibility-badge {
        display: inline-flex;
        align-items: center;
        gap: 0.4rem;
        padding: 0.35rem 0.7rem;
        border-radius: 20px;
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }
      
      .visibility-visible {
        background-color: rgba(40, 167, 69, 0.15);
        color: #28a745;
      }
      
      .visibility-hidden {
        background-color: rgba(108, 117, 125, 0.15);
        color: #6c757d;
      }
      
      /* Estilo para o switch de checkbox */
      .switch-container {
        display: flex;
        align-items: flex-start;
        margin-bottom: 1.5rem;
        gap: 1rem;
      }
      
      .switch {
        position: relative;
        display: inline-block;
        width: 50px;
        height: 24px;
        margin-top: 2px;
        flex-shrink: 0;
      }
      
      .switch input {
        opacity: 0;
        width: 0;
        height: 0;
      }
      
      .slider {
        position: absolute;
        cursor: pointer;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: #ccc;
        transition: .4s;
        border-radius: 34px;
      }
      
      .slider:before {
        position: absolute;
        content: "";
        height: 16px;
        width: 16px;
        left: 4px;
        bottom: 4px;
        background-color: white;
        transition: .4s;
        border-radius: 50%;
      }
      
      input:checked + .slider {
        background-color: #2196F3;
      }
      
      input:focus + .slider {
        box-shadow: 0 0 1px #2196F3;
      }
      
      input:checked + .slider:before {
        transform: translateX(26px);
      }
      
      .switch-label {
        font-weight: 500;
        color: #495057;
        user-select: none;
        display: block;
        margin-bottom: 5px;
      }
      
      .switch-description {
        font-size: 0.8rem;
        color: #6c757d;
        margin: 0;
        line-height: 1.4;
      }
    `;
    document.head.appendChild(style);
    
    // Limpar ao desmontar
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Impedir o scroll quando o modal estiver aberto
  useEffect(() => {
    if (showModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showModal]);
  
  // Adicionar efeito para rolar o modal para o topo quando for aberto
  useEffect(() => {
    if (showModal) {
      // Resetar a posição de scroll do modal quando aberto
      setTimeout(() => {
        const modalBody = document.querySelector('.modal-scrollable-body');
        if (modalBody) {
          modalBody.scrollTop = 0;
        }
      }, 100);
    }
  }, [showModal, activeTab]);

  // Manipular mudanças nos campos do formulário
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' 
        ? checked 
        : (name === 'preco' || name === 'categoria_id' || name === 'estoque') 
          ? Number(value) 
          : value
    });
  };

  // Abrir o modal para adicionar um novo produto
  const handleAddNew = () => {
    setFormData({
      id: null,
      nome: '',
      descricao: '',
      preco: '',
      categoria_id: categories.length > 0 ? categories[0].id : 1,
      estoque: 0,
      ativo: true,
      visivel_no_catalogo: true,
      vinculado_credencial_expositor: false,
      vinculado_montador: false,
      vinculado_prestador: false // INICIALIZAR NOVO CAMPO
    });
    setIsEditing(false);
    setShowModal(true);
    setActiveTab('dados');
  };

  // Abrir o modal para editar um produto existente
  const handleEdit = (product) => {
    // Garantir que todos os campos tenham valores padrão se forem nulos
    const visivel_no_catalogo = product.visivel_no_catalogo === null ? true : product.visivel_no_catalogo;
    const vinculado_credencial_expositor = product.vinculado_credencial_expositor === null ? false : product.vinculado_credencial_expositor;
    const vinculado_montador = product.vinculado_montador === null ? false : product.vinculado_montador;
    const vinculado_prestador = product.vinculado_prestador === null ? false : product.vinculado_prestador; // TRATAR NOVO CAMPO
    
    setFormData({
      ...product,
      visivel_no_catalogo,
      vinculado_credencial_expositor,
      vinculado_montador,
      vinculado_prestador // ADICIONAR NOVO CAMPO
    });
    
    setIsEditing(true);
    setShowModal(true);
    setActiveTab('dados');
  };

  // Excluir um produto
  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir este produto?')) {
      try {
        const { error } = await supabase
          .from('caex_produtos')
          .delete()
          .eq('id', id);
        
        if (error) throw error;
        
        // Atualizar a lista de produtos
        setProducts(products.filter(product => product.id !== id));
      } catch (error) {
        console.error('Erro ao excluir produto:', error);
        alert('Não foi possível excluir o produto. Por favor, tente novamente.');
      }
    }
  };

  // Obter nome da categoria por ID
  const getCategoryName = (categoryId) => {
    console.log('Buscando categoria com ID:', categoryId, 'no array:', categories);
    
    if (!Array.isArray(categories)) {
      console.warn('categories não é um array válido:', categories);
      return 'Categoria indisponível';
    }
    
    const category = categories.find(cat => cat.id === categoryId);
    
    if (!category) {
      console.warn(`Categoria ID ${categoryId} não encontrada`);
      return 'Categoria não encontrada';
    }
    
    console.log('Categoria encontrada:', category);
    return category.nome || `Categoria ${categoryId}`;
  };

  // Salvar o formulário (adicionar novo ou atualizar existente)
  const handleSubmit = async () => {
    try {
      // Validar dados do formulário
      if (!formData.nome || !formData.preco) {
        alert('Por favor, preencha os campos obrigatórios.');
        return;
      }

      // Adicionar timestamp de atualização
      const updatedData = {
        ...formData,
        updated_at: new Date()
      };
      
      if (isEditing) {
        // Atualizar produto existente
        const { error } = await supabase
          .from('caex_produtos')
          .update(updatedData)
          .eq('id', formData.id);
        
        if (error) throw error;
        
        // Atualizar a lista de produtos localmente
        setProducts(products.map(product => 
          product.id === formData.id ? updatedData : product
        ));
      } else {
        // Para novos produtos, remover o campo ID para permitir auto-incremento
        const { id, ...dataWithoutId } = updatedData;
        console.log('Dados para inserção (sem ID):', dataWithoutId);
        
        // Adicionar novo produto
        const { data, error } = await supabase
          .from('caex_produtos')
          .insert([dataWithoutId])
          .select();
        
        if (error) throw error;
        
        console.log('Produto adicionado com sucesso:', data);
        
        // Adicionar o novo produto à lista
        setProducts([data[0], ...products]);
      }
      
      // Fechar o modal e resetar o formulário
      setShowModal(false);
      setFormData({
        id: null,
        nome: '',
        descricao: '',
        preco: '',
        categoria_id: categories.length > 0 ? categories[0].id : 1,
        estoque: 0,
        ativo: true,
        visivel_no_catalogo: true,
        vinculado_credencial_expositor: false,
        vinculado_montador: false,
        vinculado_prestador: false // RESETAR NOVO CAMPO
      });
    } catch (error) {
      console.error('Erro ao salvar produto:', error);
      alert(`Não foi possível salvar o produto. Erro: ${error.message || 'Desconhecido'}`);
    }
  };

  // Filtrar produtos com base na categoria selecionada
  const filteredProducts = filterCategory === 0 
    ? products 
    : products.filter(product => product.categoria_id === filterCategory);

  // Obter cor baseada na categoria
  const getCategoryColor = (categoryId) => {
    const colors = {
      1: '#6f42c1', // Roxo
      2: '#ffc107', // Amarelo
      3: '#28a745', // Verde
      4: '#17a2b8', // Azul claro
      5: '#dc3545', // Vermelho
    };
    return colors[categoryId] || '#6c757d'; // Cinza como cor padrão
  };

  // Obter ícone baseado na categoria
  const getCategoryIcon = (categoryId) => {
    switch (categoryId) {
      case 1: return <FaBox />;
      case 2: return <FaShoppingCart />;
      case 3: return <FaUsers />;
      case 4: return <FaTags />;
      case 5: return <FaTags />;
      default: return <FaBox />;
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-hub">
          <div className="dashboard-header">
            <div className="dashboard-title-section">
              <h1 className="dashboard-main-title">
                <FaShoppingCart className="dashboard-title-icon" />
                Gestão de Produtos
              </h1>
            </div>
          </div>
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-gray-900"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-hub">
          <div className="dashboard-header">
            <div className="dashboard-title-section">
              <h1 className="dashboard-main-title">
                <FaShoppingCart className="dashboard-title-icon" />
                Gestão de Produtos
              </h1>
            </div>
          </div>
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative my-4" role="alert">
            <strong className="font-bold">Erro: </strong>
            <span className="block sm:inline">{error}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-hub">
        {/* Header Section */}
        <div className="dashboard-header">
          <div className="dashboard-title-section">
            <h1 className="dashboard-main-title">
              <FaShoppingCart className="dashboard-title-icon" />
              Gestão de Produtos
            </h1>
            <p className="dashboard-subtitle">
              Gerenciamento do catálogo de produtos e itens adicionais
            </p>
          </div>
        </div>

        {/* Área de botão e filtro - um em cada extremidade */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '20px',
          width: '100%'
        }}>
          {/* Botão no canto esquerdo */}
          {hasPermission('all') && (
            <button 
              onClick={handleAddNew}
              className="bg-black text-white px-4 py-2 rounded-md hover:bg-gray-800 transition-all flex items-center gap-2"
              style={{ boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}
            >
              <FaPlus />
              Adicionar Produto
            </button>
          )}
          
          {/* Filtro no canto direito */}
          <div className="flex items-center">
            <FaFilter className="mr-2 text-gray-600" />
            <label className="mr-2 text-gray-700">Filtrar por categoria:</label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(Number(e.target.value))}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black"
              style={{ boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)' }}
            >
              <option value={0}>Todas as categorias</option>
              {/* Categorias carregadas dinamicamente */}
              {categories.map((cat) => (
                <option key={`filter-cat-${cat.id}`} value={cat.id}>
                  {cat.nome}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Tabela de produtos com estilo similar ao ExhibitorManagement */}
        <div className="modern-table-card" style={{ animation: 'fadeInUp 0.6s ease-out' }}>
          <div className="modern-card-header">
            <h5 className="modern-card-title">
              <FaShoppingCart className="card-title-icon" />
              Lista de Produtos
            </h5>
          </div>
          <div className="modern-card-body">
            {filteredProducts.length === 0 ? (
              <div className="bg-gray-100 border border-gray-300 text-gray-700 px-4 py-8 rounded-lg text-center my-4">
                <p className="text-xl">Nenhum produto encontrado.</p>
                {filterCategory !== 0 && (
                  <p className="mt-2">Tente selecionar outra categoria ou criar um novo produto.</p>
                )}
              </div>
            ) : (
              <div className="modern-table-container">
                <table className="modern-table">
                  <thead>
                    <tr>
                      <th>Nome</th>
                      <th>Descrição</th>
                      <th>Categoria</th>
                      <th>Preço</th>
                      <th>Estoque</th>
                      <th>Status</th>
                      <th>Visível para expositor</th>
                      <th>Vinculações</th>
                      <th className="text-right">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.map((product) => {
                      const categoryColor = getCategoryColor(product.categoria_id);
                      const isVisible = product.visivel_no_catalogo !== false; // Tratar valores nulos como true
                      const isCredencialLinked = product.vinculado_credencial_expositor === true;
                      const isMontadorLinked = product.vinculado_montador === true;
                      const isPrestadorLinked = product.vinculado_prestador === true; // NOVA VERIFICAÇÃO
                      
                      return (
                        <tr key={product.id}>
                          <td className="product-name">{product.nome}</td>
                          <td>{product.descricao ? product.descricao.substring(0, 50) + (product.descricao.length > 50 ? '...' : '') : '-'}</td>
                          <td>
                            <span className="badge-label" style={{ color: categoryColor, fontWeight: 'bold' }}>
                              {getCategoryName(product.categoria_id)}
                            </span>
                          </td>
                          <td>R$ {product.preco.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td>{product.estoque || 0}</td>
                          <td>
                            <span className="modern-badge" style={{ 
                              backgroundColor: product.ativo ? '#28a745' : '#dc3545',
                              color: 'white',
                              padding: '0.4rem 0.8rem',
                              borderRadius: '20px',
                              fontSize: '0.75rem',
                              fontWeight: '600',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}>
                              {product.ativo ? 'Ativo' : 'Inativo'}
                            </span>
                          </td>
                          <td>
                            <span className={`visibility-badge ${isVisible ? 'visibility-visible' : 'visibility-hidden'}`}>
                              {isVisible ? (
                                <>
                                  <FaEye size={12} />
                                  Visível
                                </>
                              ) : (
                                <>
                                  <FaEyeSlash size={12} />
                                  Oculto
                                </>
                              )}
                            </span>
                          </td>
                          <td>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                              {isCredencialLinked && (
                                <span className="link-badge link-credencial">
                                  <FaIdCard size={10} />
                                  Credencial
                                </span>
                              )}
                              {isMontadorLinked && (
                                <span className="link-badge link-montador">
                                  <FaTools size={10} />
                                  Montador
                                </span>
                              )}
                              {/* NOVO BADGE PARA PRESTADOR */}
                              {isPrestadorLinked && (
                                <span className="link-badge link-prestador">
                                  <FaUserTie size={10} />
                                  Prestador
                                </span>
                              )}
                              {!isCredencialLinked && !isMontadorLinked && !isPrestadorLinked && (
                                <span style={{ fontSize: '0.8rem', color: '#6c757d' }}>-</span>
                              )}
                            </div>
                          </td>
                          <td className="text-right">
                            {hasPermission('all') && (
                              <div className="flex justify-end gap-2">
                                <button
                                  onClick={() => handleEdit(product)}
                                  style={{
                                    ...modalStyles.footerButton,
                                    ...modalStyles.cancelButton
                                  }}
                                  className="btn-cancel"
                                >
                                  <FaEdit className="mr-1" />
                                  Editar
                                </button>

                                <button
                                  onClick={() => handleDelete(product.id)}
                                  style={{
                                    ...modalStyles.footerButton,
                                    ...modalStyles.submitButton,
                                    background: '#dc3545' // vermelho elegante
                                  }}
                                  className="btn-submit"
                                >
                                  <FaTrash className="mr-1" />
                                  Excluir
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Modal para adicionar/editar produto */}
        {showModal && (
          <div style={modalStyles.overlay}>
            <div style={modalStyles.content}>
              <div style={modalStyles.header}>
                <h2 style={modalStyles.title}>
                  {isEditing 
                    ? <FaEdit style={{color: '#6c757d'}} /> 
                    : <FaPlus style={{color: '#6c757d'}} />
                  }
                  {isEditing ? 'Editar Produto' : 'Novo Produto'}
                </h2>
                <button 
                  onClick={() => setShowModal(false)}
                  style={modalStyles.closeButton}
                  className="modal-close-btn"
                  aria-label="Fechar"
                >
                  ✕
                </button>
              </div>

              <div style={modalStyles.tabs}>
                <div 
                  className={`modal-tab ${activeTab === 'dados' ? 'modal-tab-active' : ''}`}
                  onClick={() => setActiveTab('dados')}
                >
                  Dados do Produto
                </div>
                <div 
                  className={`modal-tab ${activeTab === 'config' ? 'modal-tab-active' : ''}`}
                  onClick={() => setActiveTab('config')}
                >
                  Configurações Adicionais
                </div>
              </div>
              
              <div style={modalStyles.body} className="modal-scrollable-body">
                {activeTab === 'dados' && (
                  <>
                    <div className="form-group">
                      <label className="form-label" htmlFor="nome">
                        Nome do Produto <span style={modalStyles.requiredField}>*</span>
                      </label>
                      <input
                        type="text"
                        id="nome"
                        name="nome"
                        value={formData.nome}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="Digite o nome do produto"
                        required
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label" htmlFor="categoria_id">
                        Categoria <span style={modalStyles.requiredField}>*</span>
                      </label>
                      <select
                        id="categoria_id"
                        name="categoria_id"
                        value={formData.categoria_id}
                        onChange={handleChange}
                        className="form-input"
                      >
                        {/* Categorias dinâmicas */}
                        {categories.map((cat) => (
                          <option key={`modal-cat-${cat.id}`} value={cat.id}>
                            {cat.nome}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label" htmlFor="descricao">
                        Descrição
                      </label>
                      <textarea
                        id="descricao"
                        name="descricao"
                        value={formData.descricao || ''}
                        onChange={handleChange}
                        rows="4"
                        className="form-input"
                        placeholder="Digite a descrição do produto"
                      ></textarea>
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label" htmlFor="preco">
                        Preço (R$) <span style={modalStyles.requiredField}>*</span>
                      </label>
                      <input
                        type="number"
                        id="preco"
                        name="preco"
                        value={formData.preco}
                        onChange={handleChange}
                        min="0"
                        step="0.01"
                        className="form-input"
                        placeholder="0,00"
                        required
                      />
                    </div>
                    
                    <div style={modalStyles.requiredNotice}>
                      * Campos obrigatórios
                    </div>
                  </>
                )}
                
                {activeTab === 'config' && (
                  <>
                    <div className="form-group">
                      <label className="form-label" htmlFor="estoque">
                        Estoque
                      </label>
                      <input
                        type="number"
                        id="estoque"
                        name="estoque"
                        value={formData.estoque || 0}
                        onChange={handleChange}
                        min="0"
                        className="form-input"
                        placeholder="0"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label" htmlFor="imagem_url">
                        URL da Imagem
                      </label>
                      <input
                        type="text"
                        id="imagem_url"
                        name="imagem_url"
                        value={formData.imagem_url || ''}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="https://exemplo.com/imagem.jpg"
                      />
                    </div>

                    <div className="switch-container">
                      <label className="switch">
                        <input
                          type="checkbox"
                          id="ativo"
                          name="ativo"
                          checked={formData.ativo}
                          onChange={handleChange}
                        />
                        <span className="slider"></span>
                      </label>
                      <div>
                        <span className="switch-label">Produto ativo</span>
                        <p className="switch-description">
                          Determina se o produto está disponível no sistema
                        </p>
                      </div>
                    </div>
                    
                    <div className="switch-container" style={{ marginTop: '1.5rem' }}>
                      <label className="switch">
                        <input
                          type="checkbox"
                          id="visivel_no_catalogo"
                          name="visivel_no_catalogo"
                          checked={formData.visivel_no_catalogo}
                          onChange={handleChange}
                        />
                        <span className="slider"></span>
                      </label>
                      <div>
                        <span className="switch-label">Mostrar para expositor</span>
                        <p className="switch-description">
                          Se desativado, o produto ficará oculto no catálogo para expositores, mas ainda estará 
                          disponível para uso por administradores
                        </p>
                      </div>
                    </div>

                    {/* Vincular à Credencial do Expositor */}
                    <div className="switch-container" style={{ marginTop: '1.5rem' }}>
                      <label className="switch">
                        <input
                          type="checkbox"
                          id="vinculado_credencial_expositor"
                          name="vinculado_credencial_expositor"
                          checked={formData.vinculado_credencial_expositor}
                          onChange={handleChange}
                        />
                        <span className="slider"></span>
                      </label>
                      <div>
                        <span className="switch-label">
                          <FaIdCard style={{ marginRight: '0.5rem', color: '#007bff' }} />
                          Vincular à Credencial do Expositor
                        </span>
                        <p className="switch-description">
                          Quando ativo, este produto será automaticamente vinculado à credencial do expositor 
                          durante o processo de cadastro ou compra
                        </p>
                      </div>
                    </div>

                    {/* Vincular ao Montador */}
                    <div className="switch-container" style={{ marginTop: '1.5rem' }}>
                      <label className="switch">
                        <input
                          type="checkbox"
                          id="vinculado_montador"
                          name="vinculado_montador"
                          checked={formData.vinculado_montador}
                          onChange={handleChange}
                        />
                        <span className="slider"></span>
                      </label>
                      <div>
                        <span className="switch-label">
                          <FaTools style={{ marginRight: '0.5rem', color: '#ffc107' }} />
                          Vincular ao Montador
                        </span>
                        <p className="switch-description">
                          Quando ativo, este produto será automaticamente associado aos serviços de montagem 
                          oferecidos pelos montadores credenciados
                        </p>
                      </div>
                    </div>

                    {/* NOVO: Vincular ao Prestador */}
                    <div className="switch-container" style={{ marginTop: '1.5rem' }}>
                      <label className="switch">
                        <input
                          type="checkbox"
                          id="vinculado_prestador"
                          name="vinculado_prestador"
                          checked={formData.vinculado_prestador}
                          onChange={handleChange}
                        />
                        <span className="slider"></span>
                      </label>
                      <div>
                        <span className="switch-label">
                          <FaUserTie style={{ marginRight: '0.5rem', color: '#28a745' }} />
                          Vincular ao Prestador
                        </span>
                        <p className="switch-description">
                          Quando ativo, este produto será automaticamente vinculado aos serviços oferecidos 
                          pelos prestadores de serviços credenciados
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </div>
              
              <div style={modalStyles.footer}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{...modalStyles.footerButton, ...modalStyles.cancelButton}}
                  className="btn-cancel"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  style={{...modalStyles.footerButton, ...modalStyles.submitButton}}
                  className="btn-submit"
                >
                  {isEditing ? 'Atualizar' : 'Salvar'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductManagement;